/**
 * The UNGUIDED lesson host, driven by the URL:
 *
 *   /cart?sim=ordering-lesson-1
 *
 * Deliberately the inverse of `TourOverlay`. That one darkens the screen,
 * spotlights one element and swallows the tap — the learner never touches the
 * real control. This one shows the objective and a checklist, and then gets out
 * of the way: the container is `pointer-events-none` so every tap lands on the
 * app underneath, and the panel collapses to a small pill so nothing important
 * stays hidden.
 *
 * It carries no step cursor. There is no "next" in an unguided run — the
 * learner decides what to do, and the checklist reports what the app observed.
 */
import { Check, ChevronDown, ClipboardList, Lightbulb } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker, useLocation, useNavigate, useSearchParams } from 'react-router'
import {
  DESC_SIZES,
  META_SIZES,
  TITLE_SIZES,
  useAccessibility,
} from '@/accessibility/use-accessibility'
import { resolveContentTemplates } from '@/content/templates'
import { applyCartStartParams } from '@/hooks/use-cart-seed'
import { DEFAULT_LANG, useLang } from '@/hooks/use-lang'
import { cn } from '@/lib/utils'
import { LangToggle } from '@/player/lang-toggle'
import { useLessonProgress } from '@/player/lesson-progress'
import { useSimProgress } from '@/player/sim-progress'
import { evaluateTasks, routeMatches } from '@/player/task-progress'
import { loadLesson } from '@/player/tour-content'
import { UnguidedTour, type UnguidedTourStep } from '@/player/UnguidedTour'
import type { SubModule } from '@/schema'
import { useOrderingStore } from '@/screens/ordering/store'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog'

// data-tour values blocked in every sim regardless of route —
// actions that are never part of any lesson's task flow.
// NOTE: 'cart-delete-line' was intentionally removed — ordering-lesson-2 task d0
// requires the learner to delete a cart line, so it cannot be globally blocked.
const SIM_BLOCKED: ReadonlySet<string> = new Set(['cart-delete-all'])

// Shown exactly once, the first time a learner enters any unguided lesson.
const UNGUIDED_TOUR_KEY = 'fpg:unguided-tour-seen'

// How far the pointer must travel before a press on the collapsed pill counts
// as a drag. No tap is pixel-perfect, and without a floor the 1-2px of jitter
// in an ordinary tap both swallowed the expand click and teleported the pill.
const DRAG_THRESHOLD = 5
// The collapsed pill's diameter (`size-14`) plus the gutter the clamp keeps
// between it and the edge of the visible area.
const PILL_SIZE = 56
const PILL_EDGE_GAP = 8

/**
 * The area actually visible to the learner, tracked live.
 *
 * The same hook `TourOverlay` keeps private, plus `width` — this overlay is the
 * only one with something draggable to clamp horizontally. On iOS
 * `window.innerHeight` does NOT shrink when the software keyboard opens, only
 * `visualViewport.height` does, so anything pinned to the layout viewport's
 * bottom edge ends up behind the keyboard. `offsetTop` is how far iOS has
 * shifted the visible area down to keep the focused input in view.
 */
function useVisualViewport(): { width: number; height: number; offsetTop: number } {
  const [vp, setVp] = useState(() => ({
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
    offsetTop: window.visualViewport?.offsetTop ?? 0,
  }))
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const update = () => setVp({ width: vv.width, height: vv.height, offsetTop: vv.offsetTop })
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])
  return vp
}

/**
 * Keeps the dragged pill fully inside the visible area.
 *
 * `pos` is the pill's CENTRE — the inline style offsets it by -50% on both
 * axes — and it outlives every collapse, expand and route change for the life
 * of the run. Without a clamp, `setPointerCapture` happily tracks a finger
 * past the screen edge and parks the pill somewhere the learner can never
 * reach again. Applied on every render, not just on drop, so a pill left low
 * comes back into view when the keyboard shrinks the viewport under it.
 *
 * Exported as a pure function: the clamp is the part worth proving, and no
 * jsdom test can see where the pill actually lands.
 */
export function clampPillPos(
  pos: { x: number; y: number },
  vp: { width: number; height: number },
): { x: number; y: number } {
  const half = PILL_SIZE / 2 + PILL_EDGE_GAP
  // A viewport narrower or shorter than the pill itself would invert the
  // bounds; the near edge wins so the pill stays put rather than jumping.
  return {
    x: Math.min(Math.max(pos.x, half), Math.max(half, vp.width - half)),
    y: Math.min(Math.max(pos.y, half), Math.max(half, vp.height - half)),
  }
}

/**
 * Tone classes for a task label. A ticked task reads as struck-through muted
 * regardless of the bold-text accessibility preference, which only applies to
 * labels still outstanding.
 */
function taskLabelTone(isDone: boolean, boldText: boolean): string {
  if (isDone) return 'text-muted-foreground line-through'
  if (boldText) return 'font-bold text-foreground'
  return 'text-foreground'
}

export function TaskOverlay() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const lang = useLang()
  const { t } = useTranslation('player')
  const { width: vpWidth, height: vpHeight, offsetTop: vpOffsetTop } = useVisualViewport()
  const [subModule, setSubModule] = useState<SubModule | null>(null)
  const [expanded, setExpanded] = useState(true)
  const [exitDialogOpen, setExitDialogOpen] = useState(false)
  const [activeHintId, setActiveHintId] = useState<string | null>(null)
  const [tourStep, setTourStep] = useState<number | null>(null)

  /**
   * `?sim=` only STARTS a run; the store is what keeps it alive.
   *
   * The URL cannot be the source of truth here the way `?tour=` is for the
   * guided player. Real screens navigate with bare paths — the home screen's
   * cart button goes to `/cart`, dropping every query parameter — so an
   * unguided run keyed on the URL dies on the learner's first tap. The guided
   * player only survives this because it re-navigates to its own step URL on
   * every render, which an unguided run must not do: rewriting the URL under a
   * learner who is navigating freely is exactly the guidance we are removing.
   */
  const { fontScale, boldText } = useAccessibility()

  const simParam = params.get('sim')

  // One primitive selector per value. Zustand v5 has no shallow compare by
  // default, so returning a fresh object from a selector re-renders forever —
  // the pattern every screen in this codebase already follows.
  const lines = useOrderingStore((s) => s.lines)
  const qpo = useOrderingStore((s) => s.qpo)
  const heldQpos = useOrderingStore((s) => s.heldQpos)

  const simId = useSimProgress((s) => s.simId)
  const visitedRoutes = useSimProgress((s) => s.visitedRoutes)
  const milestones = useSimProgress((s) => s.milestones)
  const completed = useSimProgress((s) => s.completed)
  const start = useSimProgress((s) => s.start)
  const visit = useSimProgress((s) => s.visit)
  const complete = useSimProgress((s) => s.complete)
  const stop = useSimProgress((s) => s.stop)

  /**
   * Remembers a run the learner explicitly ended, so it cannot resurrect.
   *
   * Exiting clears the store, but the URL still carries `?sim=` until the
   * navigation away commits — and in that gap the effect below sees "a param,
   * no active run" and starts the very run that was just ended. Cleared when
   * the param goes away, so re-entering the same lesson later still works.
   */
  const endedRef = useRef<string | null>(null)

  // Tracks which simId was most recently fresh-started so the loadLesson
  // callback can seed startParams exactly once after resetAll().
  const pendingCartSeedForRef = useRef<string | null>(null)

  // Adopt the run named by `?sim=`, or start one when the learner switches to a
  // different lesson. Re-entry after the URL dropped `?sim=` is NOT a fresh
  // start — see the note below.
  useEffect(() => {
    if (!simParam) {
      endedRef.current = null
      return
    }
    if (endedRef.current === simParam) return
    // Only a genuine lesson switch starts a run here. A null -> non-null
    // transition must NOT restart: LessonStart (which renders under TourLayout,
    // so this overlay is already mounted) has just called start() itself, and
    // restarting would wipe the store it seeded. It also kept a mid-lesson hop
    // that dropped `?sim=` from throwing away completed tasks on return.
    if (simParam !== simId) {
      start(simParam)
      pendingCartSeedForRef.current = simParam
    }
  }, [simParam, simId, start])

  // Starting a guided lesson ends any unguided run. Without this the two
  // players would stack — the coach-mark scrim over a live checklist — because
  // the sim now outlives the URL that started it.
  const tourParam = params.get('tour')
  useEffect(() => {
    if (tourParam && simId) stop()
  }, [tourParam, simId, stop])

  // A new run starting means any leftover exit-dialog state from the previous
  // run must be cleared. TaskOverlay never unmounts, so exitDialogOpen keeps
  // its value across runs; without this reset the dialog re-appears the moment
  // simId becomes truthy again on a second visit.
  useEffect(() => {
    if (simId) setExitDialogOpen(false)
  }, [simId])

  // Show the one-time onboarding tour the very first time a sim becomes active.
  useEffect(() => {
    if (!simId) return
    try {
      if (!localStorage.getItem(UNGUIDED_TOUR_KEY)) setTourStep(0)
    } catch {
      // Storage blocked — silently skip the tour.
    }
  }, [simId])

  // Load the active run's tasks. `loadLesson` already returns the whole
  // sub-module, so no new content loader is needed.
  useEffect(() => {
    if (!simId) {
      setSubModule(null)
      return
    }
    let live = true
    loadLesson(simId, lang)
      .then((lesson) => {
        if (!live) return
        setSubModule(lesson?.subModule ?? null)
        // Re-apply cart startParams after resetAll() wipes the store on fresh
        // starts. Only fires once per start(), not on lang-change reloads.
        if (pendingCartSeedForRef.current === simId && lesson?.subModule.startParams) {
          applyCartStartParams(lesson.subModule.startParams)
          pendingCartSeedForRef.current = null
        }
      })
      .catch(() => {
        if (live) setSubModule(null)
      })
    return () => {
      live = false
    }
  }, [simId, lang])

  /**
   * Keep the run's IDENTITY on the URL.
   *
   * `useRole()` re-reads `?role=` on every render and falls back to the default
   * storekeeper, and no `navigate()` in the app preserves query parameters — so
   * without this an unguided learner reverts to a non-approver on their first
   * tap and can never reach Lesson 5's approve/reject.
   *
   * This is not the guidance we removed. It restores who the learner IS, never
   * where they should go, and the host-app contract already says role travels
   * as a URL parameter. `startParams` is deliberately NOT re-applied here —
   * re-seeding `cart=3` on every navigation would wipe the learner's cart.
   */
  const sessionParams = subModule?.sessionParams
  useEffect(() => {
    if (!simId) return
    const wanted = new Map(Object.entries(sessionParams ?? {}))
    // `lang` rides along for the same reason `role` does: it lives only on the
    // URL, no navigate() preserves it, and an unguided learner navigates
    // freely — so without this the checklist reverts to English on their first
    // tap. `useLang()` already keeps the COPY correct; this keeps the URL (and
    // therefore the toggle's own state, and a refresh) correct too.
    if (lang !== DEFAULT_LANG) wanted.set('lang', lang)
    if (wanted.size === 0) return
    const missing = [...wanted].filter(([k, v]) => params.get(k) !== v)
    if (missing.length === 0) return
    const next = new URLSearchParams(params)
    for (const [k, v] of missing) next.set(k, v)
    setParams(next, { replace: true })
  }, [simId, sessionParams, params, setParams, lang])

  // Record every route reached during the run. Some tasks can only be proven
  // this way — nothing in the store records that My Cart was opened.
  useEffect(() => {
    if (simId) visit(location.pathname)
  }, [simId, location.pathname, visit])

  const tasks = useMemo(() => subModule?.tasks ?? [], [subModule])

  const hasHints = useMemo(() => tasks.some((task) => !!task.hint), [tasks])

  // Steps shown in the one-time onboarding tour.
  // The collapse and expand steps are interactive: the learner physically
  // performs the action rather than reading a Next button.
  // The hint step is only included when the lesson has hints.
  const tourSteps = useMemo(
    (): UnguidedTourStep[] => [
      {
        kind: 'narrative',
        heading: t('tourWelcomeHeading'),
        body: t('tourWelcomeBody'),
      },
      {
        kind: 'spotlight',
        target: 'unguided-tasks',
        heading: t('tourTasksHeading'),
        body: t('tourTasksBody'),
      },
      {
        kind: 'collapse',
        target: 'unguided-collapse',
        heading: t('tourCollapseHeading'),
        body: t('tourCollapseBody'),
      },
      {
        kind: 'expand',
        target: 'unguided-pill',
        heading: t('tourExpandHeading'),
        body: t('tourExpandBody'),
      },
      ...(hasHints
        ? [
            {
              kind: 'spotlight' as const,
              target: 'unguided-hint',
              heading: t('tourHintHeading'),
              body: t('tourHintBody'),
            },
          ]
        : []),
      {
        kind: 'spotlight',
        target: 'unguided-exit',
        heading: t('tourExitHeading'),
        body: t('tourExitBody'),
      },
    ],
    [t, hasHints],
  )

  const advanceTour = useCallback(() => {
    setTourStep((prev) => {
      if (prev === null) return null
      const next = prev + 1
      if (next >= tourSteps.length) {
        try {
          localStorage.setItem(UNGUIDED_TOUR_KEY, '1')
        } catch {}
        return null
      }
      return next
    })
  }, [tourSteps.length])

  const dismissTour = useCallback(() => {
    try {
      localStorage.setItem(UNGUIDED_TOUR_KEY, '1')
    } catch {}
    setTourStep(null)
  }, [])

  const earned = useMemo(
    () => evaluateTasks(tasks, { lines, qpo, heldQpos, visitedRoutes, milestones }, completed),
    [tasks, lines, qpo, heldQpos, visitedRoutes, milestones, completed],
  )

  // Latch anything newly earned back into the store so it survives the app
  // destroying its own evidence.
  useEffect(() => {
    for (const id of earned) if (!completed.includes(id)) complete(id)
  }, [earned, completed, complete])

  // The first incomplete task that has a hint — used to anchor the tour
  // spotlight on the hint button the learner is most likely to need.
  const firstHintTaskId = useMemo(
    () => tasks.find((task) => !!task.hint && !earned.includes(task.id))?.id ?? null,
    [tasks, earned],
  )

  const exit = useCallback(() => {
    endedRef.current = simParam
    stop()
    void navigate('/', { replace: true })
  }, [simParam, stop, navigate])

  // Block any navigation to /lesson-detail/... while the sim is live.
  // Uses the live store state (not the React snapshot) so that exit() — which
  // calls stop() synchronously before navigating — is never blocked itself.
  const blocker = useBlocker(({ nextLocation }) => {
    const liveSimId = useSimProgress.getState().simId
    return !!liveSimId && nextLocation.pathname.startsWith('/lesson-detail')
  })
  useEffect(() => {
    if (blocker.state === 'blocked') blocker.reset()
  }, [blocker])

  const doneCount = earned.length
  const total = tasks.length
  const allDone = total > 0 && doneCount === total

  /**
   * Every task ticked: record the stage, end the run, and show the learner
   * that it ended.
   *
   * `requiresUnguided` is hard `true` here and does not need `hasUnguidedStage`
   * to say so: `allDone` requires `total > 0`, so reaching this line is itself
   * proof the lesson has tasks. This records the UNGUIDED stage only — the
   * lesson is complete once `TourOverlay` has recorded the guided one, in
   * whichever order the two arrive.
   *
   * `stop()` before navigating, and `endedRef` set exactly as `exit()` sets it.
   * A run outlives the URL that started it (see `simParam` above), so without
   * this the checklist panel would float over the completion screen, and the
   * `?sim=` still on the URL would restart the very run that just finished in
   * the gap before the navigation commits.
   *
   * Abandoning is untouched: `exit()` still records nothing and still goes
   * home, and nothing on this path runs unless every task is genuinely earned.
   */
  const finishUnguided = useCallback(
    (lessonId: string) => {
      // Deduped per (lesson, stage) inside the store, so the effect below and
      // the footer button cannot double-report between them.
      try {
        useLessonProgress.getState().markStageDone(lessonId, 'unguided', true)
      } catch {
        // `persist` writes to localStorage, which throws in private browsing
        // and restricted WebViews. This runs straight from an effect and the
        // app mounts no error boundary, so an escaping throw white-screens the
        // learner on the last step. Mirrors the guard in TourOverlay.finish.
      }
      endedRef.current = simParam
      stop()
      void navigate(`/lesson/${lessonId}/complete`, { replace: true })
    },
    [simParam, stop, navigate],
  )

  // The run id is passed in rather than read from the closure: `stop()` clears
  // it, and the tasks it was loaded from outlive that by a render — so this
  // effect can re-enter with `allDone` still true and no run left to finish.
  useEffect(() => {
    if (allDone && simId) finishUnguided(simId)
  }, [allDone, simId, finishUnguided])

  // Tasks that are currently eligible (not done, all `after` deps satisfied).
  // These — and only these — contribute to blocking and allowedTargets.
  const activeTasks = useMemo(() => {
    const done = new Set(earned)
    return tasks.filter((task) => {
      if (done.has(task.id)) return false
      return !(task.after?.some((id) => !done.has(id)) ?? false)
    })
  }, [tasks, earned])

  // Route patterns required by at least one active task (route-visited checks).
  const requiredRoutes = useMemo(() => {
    const routes = new Set<string>()
    for (const task of activeTasks) {
      for (const check of task.checks) {
        if (check.kind === 'route-visited') routes.add(check.path)
      }
    }
    return routes
  }, [activeTasks])

  // data-tour values the learner may tap while NOT yet on a required screen.
  const allowedTargets = useMemo(() => {
    const targets = new Set<string>()
    for (const task of activeTasks) {
      for (const t of task.allowedTargets ?? []) targets.add(t)
    }
    return targets
  }, [activeTasks])

  // Whether the current pathname matches any required route pattern.
  const onRequiredRoute = useMemo(
    () =>
      requiredRoutes.size === 0 ||
      [...requiredRoutes].some((p) => routeMatches(p, location.pathname)),
    [requiredRoutes, location.pathname],
  )

  // Drag-to-reposition for the collapsed pill so it never covers a critical control.
  const dragRef = useRef(false)
  const didDragRef = useRef(false)
  // Where the press started, so a move can be measured against it.
  const pressOriginRef = useRef<{ x: number; y: number } | null>(null)
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null)

  const handlePillPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    dragRef.current = true
    didDragRef.current = false
    pressOriginRef.current = { x: e.clientX, y: e.clientY }
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [])

  const handlePillPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const origin = pressOriginRef.current
    if (!dragRef.current || !origin) return
    // Below the threshold this is still a tap, not a drag: leave `didDragRef`
    // alone so the click that follows expands the panel, and leave the pill
    // where it is rather than snapping it under the finger.
    if (
      !didDragRef.current &&
      Math.hypot(e.clientX - origin.x, e.clientY - origin.y) < DRAG_THRESHOLD
    )
      return
    didDragRef.current = true
    // Client coordinates, which is the space the overlay container is pinned
    // to. Clamped at render time against the viewport of that moment.
    setDragPos({ x: e.clientX, y: e.clientY })
  }, [])

  const handlePillPointerUp = useCallback(() => {
    dragRef.current = false
    pressOriginRef.current = null
  }, [])

  const handlePillPointerCancel = useCallback(() => {
    dragRef.current = false
    didDragRef.current = false
    pressOriginRef.current = null
    setDragPos(null)
  }, [])

  const handlePillClick = useCallback(() => {
    if (didDragRef.current) {
      didDragRef.current = false
      return
    }
    setExpanded(true)
  }, [])

  // Capture-phase listener: the single source of blocking truth.
  //
  // On a WRONG route → block every tap that is not on an `allowedTargets` element
  // or inside the task panel / an open dialog.
  // On the CORRECT route → allow everything except SIM_BLOCKED elements.
  // When no route-visited tasks remain → allow everything except SIM_BLOCKED elements.
  useEffect(() => {
    if (!simId || total === 0) return

    const handle = (e: MouseEvent) => {
      const target = e.target as Element

      // Task panel and hint drawer are always interactive.
      if (target.closest('[data-task-panel]')) return
      if (target.closest('[role="dialog"]')) return
      // So is the one-time orientation tour. It is a sibling of the overlay
      // container rather than a child of the panel, and it is a plain div
      // rather than a dialog, so neither exemption above covered it — which
      // left its own Next button unclickable on every lesson whose first task
      // is on another screen (the blocker below runs on the WRONG route, which
      // is exactly where the tour opens).
      if (target.closest('[data-unguided-tour]')) return

      const tourEl = target.closest<HTMLElement>('[data-tour]')
      const tourValue = tourEl?.dataset.tour

      // Tasks tab always returns the learner to home during a sim.
      if (tourValue === 'nav-tasks') {
        e.stopPropagation()
        e.preventDefault()
        void navigate('/home')
        return
      }

      // Certain actions are never part of any lesson — block them in every sim
      // regardless of route.
      if (tourValue && SIM_BLOCKED.has(tourValue)) {
        e.stopPropagation()
        e.preventDefault()
        return
      }

      // No route-visited tasks active, or no task defines allowedTargets
      // (opt-out pattern: lesson has no navigation restrictions) → nothing more to block.
      if (requiredRoutes.size === 0 || allowedTargets.size === 0) return

      // On a correct route → full screen unlocked.
      if (onRequiredRoute) return

      // Wrong route: only allowedTargets elements pass through.
      if (tourValue && allowedTargets.has(tourValue)) return

      e.stopPropagation()
      e.preventDefault()
    }

    document.addEventListener('click', handle, { capture: true })
    return () => document.removeEventListener('click', handle, { capture: true })
  }, [simId, total, navigate, requiredRoutes, onRequiredRoute, allowedTargets])

  if (!simId || total === 0) return null

  const pillPos = dragPos ? clampPillPos(dragPos, { width: vpWidth, height: vpHeight }) : null

  return (
    <>
      {/* No dark scrim — the app is fully visible and interactive.
        Wrong-route blocking lives entirely in the capture-phase document listener
        above, which swallows the tap before the app sees it — including the
        <a href>-based navigations to non-required routes. */}
      <div
        data-task-overlay
        className="fixed right-0 left-0 z-[60] flex flex-col items-end justify-end gap-2 p-4 pointer-events-none"
        style={{
          // Pinned to the VISUAL viewport, not `inset-0`. Lesson 3's mid-lesson
          // screens are the ones with text inputs, and on iOS the layout
          // viewport does not shrink for the software keyboard — so a bottom
          // edge at `inset-0` sits behind the keyboard and takes the collapsed
          // pill with it. `TourOverlay` already pins itself the same way.
          top: vpOffsetTop,
          height: vpHeight,
        }}
      >
        {expanded ? (
          <section
            data-task-panel
            aria-label={t('practiceTasks')}
            // `min-h-0` matters now that the container is only as tall as the
            // visible area: a flex item defaults to min-height:auto and would
            // overflow the top of a keyboard-shrunk viewport rather than shrink.
            className="pointer-events-auto flex max-h-[60svh] w-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-lg"
          >
            <header className="flex items-start gap-3 border-border border-b p-4">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ClipboardList className="size-5" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <p className={cn('font-bold text-foreground', TITLE_SIZES[fontScale])}>
                  {subModule?.title.split('—')[0]?.trim() ?? t('practice')}
                </p>
                <div className="mt-0.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  <p className={cn('text-muted-foreground', META_SIZES[fontScale])}>
                    {t('of', { done: doneCount, total })}
                  </p>
                  <LangToggle />
                </div>
              </div>
              <button
                type="button"
                data-tour="unguided-collapse"
                onClick={() => setExpanded(false)}
                aria-label={t('collapseTaskList')}
                className="flex min-h-[var(--spacing-touch)] min-w-[var(--spacing-touch)] shrink-0 items-center justify-center rounded-lg text-muted-foreground"
              >
                <ChevronDown className="size-5" aria-hidden="true" />
              </button>
            </header>

            <div className="overflow-y-auto p-4">
              {subModule?.objective ? (
                <p
                  className={cn(
                    'mb-4',
                    DESC_SIZES[fontScale],
                    boldText ? 'font-bold text-foreground' : 'text-foreground',
                  )}
                >
                  {subModule.objective}
                </p>
              ) : null}

              <ol data-tour="unguided-tasks" className="flex flex-col gap-1">
                {tasks.map((task) => {
                  const isDone = earned.includes(task.id)
                  const resolvedHint = task.hint ? resolveContentTemplates(task.hint) : task.hint
                  const hintSteps = resolvedHint ? resolvedHint.split(' → ') : []
                  let activeHint: React.ReactNode = null
                  if (resolvedHint && !isDone && activeHintId === task.id) {
                    activeHint =
                      hintSteps.length > 1 ? (
                        <ol className="mt-0.5 flex flex-col gap-1.5">
                          {hintSteps.map((step, i) => (
                            <li key={step} className="flex items-start gap-2">
                              <span
                                aria-hidden="true"
                                className="mt-px flex size-[18px] shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-[10px] text-primary"
                              >
                                {i + 1}
                              </span>
                              <span
                                className={cn(
                                  'text-muted-foreground leading-snug',
                                  META_SIZES[fontScale],
                                )}
                              >
                                {step}
                              </span>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <span
                          className={cn(
                            'text-muted-foreground leading-snug',
                            META_SIZES[fontScale],
                          )}
                        >
                          {resolvedHint}
                        </span>
                      )
                  }
                  return (
                    <li key={task.id} className="flex items-start gap-3 py-1">
                      <span
                        aria-hidden="true"
                        className={cn(
                          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border',
                          isDone
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background',
                        )}
                      >
                        {isDone ? <Check className="size-3.5" /> : null}
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="flex items-start justify-between gap-2">
                          <span
                            className={cn(META_SIZES[fontScale], taskLabelTone(isDone, boldText))}
                          >
                            {task.label}
                            <span className="sr-only">
                              {isDone ? ' — done' : ' — not done yet'}
                            </span>
                          </span>
                          {task.hint && !isDone ? (
                            <button
                              type="button"
                              data-tour={task.id === firstHintTaskId ? 'unguided-hint' : undefined}
                              onClick={() =>
                                setActiveHintId((prev) => (prev === task.id ? null : task.id))
                              }
                              aria-expanded={activeHintId === task.id}
                              className={cn(
                                'inline-flex shrink-0 touch-manipulation select-none items-center gap-1 rounded-full px-2 py-0.5 font-semibold transition-colors',
                                activeHintId === task.id
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-primary/10 text-primary',
                                META_SIZES[fontScale],
                              )}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Lightbulb className="size-3.5" aria-hidden="true" />
                              {t('hint')}
                            </button>
                          ) : null}
                        </span>
                        {activeHint}
                      </span>
                    </li>
                  )
                })}
              </ol>
            </div>

            {/*
            There is no "all tasks complete — finish" button here any more. It
            was the only way out of a completed run, and it is now unreachable:
            the effect above navigates to the completion screen on the same
            commit that makes `allDone` true, and `stop()` unmounts this panel
            with it. Leaving a button nothing can ever tap would be leaving a
            second, divergent ending to maintain.
          */}
            <footer className="border-border border-t p-4">
              <button
                type="button"
                data-tour="unguided-exit"
                onClick={() => setExitDialogOpen(true)}
                className="flex min-h-[var(--spacing-touch)] w-full items-center justify-center rounded-xl border border-border px-4 font-medium text-foreground text-sm"
              >
                {t('exitPractice')}
              </button>
            </footer>
          </section>
        ) : (
          // Collapsed state is a 56px circle, not a labelled pill. It floats over
          // a pixel-faithful replica, so every pixel it occupies is a pixel of the
          // real app a learner cannot tap — a wide pill was measured covering a
          // third of the QPO vendor-acknowledgement control. Round, minimum touch
          // size, and translucent so what is underneath stays readable.
          <button
            data-task-panel
            type="button"
            onClick={handlePillClick}
            onPointerDown={handlePillPointerDown}
            onPointerMove={handlePillPointerMove}
            onPointerUp={handlePillPointerUp}
            onPointerCancel={handlePillPointerCancel}
            aria-label={`${t('practiceTasks')}, ${t('of', { done: doneCount, total })}`}
            data-tour="unguided-pill"
            // `absolute`, not `fixed`: its containing block has to be the
            // viewport-pinned container above, or `bottom-4` resolves against the
            // layout viewport and lands behind the iOS keyboard again.
            className="pointer-events-auto absolute right-4 bottom-4 flex size-14 items-center justify-center rounded-full border border-border bg-card/90 shadow-lg backdrop-blur-sm"
            style={
              pillPos
                ? {
                    touchAction: 'none',
                    left: pillPos.x,
                    top: pillPos.y,
                    right: 'auto',
                    bottom: 'auto',
                    transform: 'translate(-50%, -50%)',
                  }
                : { touchAction: 'none' }
            }
          >
            <ClipboardList className="size-6 text-primary" aria-hidden="true" />
            <span
              aria-hidden="true"
              className="-top-1 -right-1 absolute flex items-center justify-center rounded-full bg-primary px-1.5 py-0.5 font-semibold text-xs text-primary-foreground leading-none"
            >
              {doneCount}/{total}
            </span>
          </button>
        )}

        {/* Exit confirmation popup */}
        <Dialog open={exitDialogOpen} onOpenChange={(v) => !v && setExitDialogOpen(false)}>
          <DialogContent showCloseButton={false}>
            <DialogHeader>
              <DialogTitle>{t('exitThisPractice')}</DialogTitle>
              <DialogDescription>{t('progressNotSavedPractice')}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setExitDialogOpen(false)}
                className="flex min-h-[var(--spacing-touch)] flex-1 items-center justify-center rounded-xl border border-border px-4 font-semibold text-foreground text-sm"
              >
                {t('keepGoing')}
              </button>
              <button
                type="button"
                onClick={exit}
                className="flex min-h-[var(--spacing-touch)] flex-1 items-center justify-center rounded-xl bg-destructive px-4 font-bold text-destructive-foreground text-sm"
              >
                {t('exitPractice')}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Full-screen orientation tour — rendered above the task panel (z-[80])
        so its scrim covers the whole viewport, including the app underneath. */}
      {tourStep !== null && (
        <UnguidedTour
          steps={tourSteps}
          stepIndex={tourStep}
          expanded={expanded}
          onAdvance={advanceTour}
          onDismiss={dismissTour}
        />
      )}
    </>
  )
}
